/**
 * Unified Narration Sync Utility
 * 
 * This module provides comprehensive functionality to sync narration content
 * between the codebase, Firestore database, and TTS Studio.
 * 
 * It establishes Firebase as the single source of truth while providing
 * mechanisms to keep the codebase updated when needed.
 */

import { 
  collection, 
  doc, 
  getDoc,
  getDocs,
  query,
  where,
  setDoc, 
  updateDoc,
  deleteDoc,
  DocumentData,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { getStorage, ref, listAll, getDownloadURL, uploadBytes } from 'firebase/storage';
import { presentationDb, storage } from '../firebase/config';
import { narrationScript } from '../data/narrationScript';
import { dashboardNarrationScript } from '../data/dashboardNarrationScript';
import { useState, useEffect, useCallback } from 'react';
import { uploadToStorage } from '../firebase/storage-proxy';

// Define schema version locally since it can't be imported
const SCHEMA_VERSION = '1.0.0';

// Types for narrations
export type NarrationStatus = 'pending' | 'ready' | 'error';

export interface BaseNarration {
  id: string;
  type: 'slide' | 'dashboard';
  text: string;
  title: string;
  phoneticNarration: string;
  status: NarrationStatus;
  audioUrl: string | null;
  lastUpdated: Date;
}

export interface SlideNarration extends BaseNarration {
  type: 'slide';
  id: string;
  slideId: string;
  hasDynamicContent?: boolean;
}

export interface DashboardNarration extends BaseNarration {
  type: 'dashboard';
  id: string;
  script?: string;
  triggerPoint?: 'onLoad' | 'onInteraction' | 'onCompletion';
}

export type Narration = SlideNarration | DashboardNarration;

// Helper functions
const getNarrationText = (narration: Narration): string => {
  if (narration.type === 'slide') {
    return narration.text;
  } else {
    // For dashboard narrations, use text property as primary and script as fallback
    return narration.text || narration.script || '';
  }
};

const getNarrationTitle = (narration: Narration): string => {
  // Ensure consistent SentientIQ branding
  let title = narration.title || '';
  return title.replace(/SentienIQ/g, 'SentientIQ').replace(/sentieniq/gi, 'SentientIQ');
};

const getPhoneticText = (narration: Narration): string => {
  return narration.phoneticNarration || getNarrationText(narration);
};

// Create a base narration object
const createBaseNarration = (
  id: string,
  type: 'slide' | 'dashboard',
  text: string,
  title: string,
  phoneticNarration: string = '',
  status: NarrationStatus = 'pending',
  audioUrl: string | null = null
): BaseNarration => ({
  id,
  type,
  text,
  title,
  phoneticNarration,
  status,
  audioUrl,
  lastUpdated: new Date()
});

// Create a slide narration object
const createSlideNarration = (
  id: string,
  text: string,
  title: string,
  hasDynamicContent: boolean = false,
  phoneticNarration: string = '',
  status: NarrationStatus = 'pending',
  audioUrl: string | null = null
): SlideNarration => ({
  ...createBaseNarration(id, 'slide', text, title, phoneticNarration, status, audioUrl),
  type: 'slide',
  id,
  slideId: id,
  hasDynamicContent
});

// Create a dashboard narration object
const createDashboardNarration = (
  id: string,
  text: string,
  title: string,
  script: string = '',
  triggerPoint: 'onLoad' | 'onInteraction' | 'onCompletion' = 'onLoad',
  phoneticNarration: string = '',
  status: NarrationStatus = 'pending',
  audioUrl: string | null = null
): DashboardNarration => ({
  ...createBaseNarration(id, 'dashboard', text, title, phoneticNarration, status, audioUrl),
  type: 'dashboard',
  id,
  script,
  triggerPoint
});

// Core sync functions
const syncNarrationToFirebase = async (narration: Narration): Promise<boolean> => {
  try {
    const { id, type, ...data } = narration;
    const narrationRef = doc(presentationDb, 'narrations', id);

    // Generate phonetic text if not provided
    if (!data.phoneticNarration && data.text) {
      data.phoneticNarration = data.text
        // Ensure SentientIQ is spelled correctly (not SentienIQ)
        .replace(/SentienIQ/g, 'SentientIQ')
        .replace(/sentieniq/gi, 'SentientIQ')
        // Handle common medical abbreviations
        .replace(/Dr\./g, 'Doctor')
        .replace(/\bMD\b/g, 'M D')
        .replace(/\bEHR\b/g, 'E H R')
        .replace(/\bAI\b/g, 'A I')
        .replace(/\bML\b/g, 'M L')
        .replace(/\bJIMI\b/g, 'Jimmy');
    }

    const narrationData = {
      ...data,
      type,
      lastUpdated: serverTimestamp(),
      schemaVersion: SCHEMA_VERSION
    };

    await setDoc(narrationRef, narrationData, { merge: true });
    return true;
  } catch (error) {
    console.error('Error syncing narration:', error);
    return false;
  }
};

// Fetch narrations from Firebase
const fetchNarrations = async (): Promise<Narration[]> => {
  try {
    const narrations: Narration[] = [];
    
    // Fetch from the unified 'narrations' collection (single source of truth)
    const narrationsRef = collection(presentationDb, 'narrations');
    const narrationsSnapshot = await getDocs(narrationsRef);
    
    console.log(`Found ${narrationsSnapshot.size} narrations in unified collection`);
    
    narrationsSnapshot.forEach(doc => {
      const data = doc.data();
      if (data.type === 'dashboard') {
        narrations.push(createDashboardNarration(
          doc.id,
          data.text || data.narration || '',
          data.title || '',
          data.script || '',
          data.triggerPoint || 'onLoad',
          data.phoneticNarration || '',
          data.status || 'pending',
          data.audioUrl || null
        ));
      } else {
        narrations.push(createSlideNarration(
          doc.id,
          data.text || data.narration || '',
          data.title || '',
          data.hasDynamicContent || false,
          data.phoneticNarration || '',
          data.status || 'pending',
          data.audioUrl || null
        ));
      }
    });
    
    // If no narrations found in the unified collection, fallback to the script data
    if (narrations.length === 0) {
      console.log('No narrations found in Firebase, falling back to script data');
      
      // Create narrations from the script data
      narrationScript.forEach(slide => {
        if (slide.narration) {
          narrations.push(createSlideNarration(
            slide.slideId, // Use slideId from the narration script
            slide.narration,
            slide.title,
            slide.hasDynamicContent || false,
            getPhoneticText({ text: slide.narration } as any),
            'pending',
            null
          ));
        }
      });
      
      // Add dashboard narrations
      dashboardNarrationScript.forEach(dashboard => {
        if (dashboard.narration) {
          narrations.push(createDashboardNarration(
            dashboard.id, // Dashboard script uses id
            dashboard.narration,
            dashboard.title,
            '',
            dashboard.triggerPoint || 'onLoad',
            getPhoneticText({ text: dashboard.narration } as any),
            'pending',
            null
          ));
        }
      });
    }
    
    return narrations;
  } catch (error) {
    console.error('Error fetching narrations:', error);
    return [];
  }
};

// Update narration with audio
const updateNarrationWithAudio = async (
  narrationId: string,
  audioUrl: string,
  type: 'slide' | 'dashboard' = 'slide'
): Promise<boolean> => {
  try {
    const narrationRef = doc(presentationDb, 'narrations', narrationId);
    await setDoc(narrationRef, {
      audioUrl,
      status: 'ready' as NarrationStatus,
      lastUpdated: serverTimestamp()
    }, { merge: true });
    return true;
  } catch (error) {
    console.error('Error updating narration with audio:', error);
    return false;
  }
};

// React hook for narration sync
const useNarrationSync = () => {
  const [narrations, setNarrations] = useState<Narration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const narrationCollection = collection(presentationDb, 'narrations');
    
    const unsubscribe = onSnapshot(narrationCollection, 
      (snapshot) => {
        try {
          const updatedNarrations = snapshot.docs.map(doc => {
            const data = doc.data();
            if (data.type === 'dashboard') {
              return createDashboardNarration(
                doc.id,
                data.text || '',
                data.title || '',
                data.script || '',
                data.triggerPoint || 'onLoad',
                data.phoneticNarration || '',
                data.status || 'pending',
                data.audioUrl || null
              );
            } else {
              return createSlideNarration(
                doc.id,
                data.text || data.narration || '',
                data.title || '',
                data.hasDynamicContent || false,
                data.phoneticNarration || '',
                data.status || 'pending',
                data.audioUrl || null
              );
            }
          });
          setNarrations(updatedNarrations);
          setLoading(false);
        } catch (err) {
          setError(err instanceof Error ? err : new Error('Unknown error'));
          setLoading(false);
        }
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const updateNarration = async (narration: Narration) => {
    try {
      await syncNarrationToFirebase(narration);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update narration'));
      return false;
    }
  };

  return {
    narrations,
    loading,
    error,
    updateNarration,
    createSlideNarration,
    createDashboardNarration,
    syncNarrationToFirebase,
    updateNarrationWithAudio
  };
};

/**
 * Upload narration audio to Firebase Storage and return the download URL
 * 
 * @param audioBlob - The audio blob to upload
 * @param narrationId - The ID of the narration
 * @param type - The type of narration (slide or dashboard)
 * @returns Promise with the download URL
 */
const uploadNarrationAudio = async (audioBlob: Blob, narrationId: string, type: 'slide' | 'dashboard'): Promise<string> => {
  // Validate inputs
  if (!audioBlob || !(audioBlob instanceof Blob)) {
    throw new Error('Invalid audio blob');
  }

  if (!narrationId) {
    throw new Error('Narration ID is required');
  }

  if (!['slide', 'dashboard'].includes(type)) {
    throw new Error('Type must be either "slide" or "dashboard"');
  }

  // Standardize the narration ID to ensure consistent naming
  // This helps with future reconciliation between audio files and narration documents
  const standardizedNarrationId = narrationId.trim();

  // Get the storage path for a narration with standardized naming
  const getNarrationPath = (id: string, narType: 'slide' | 'dashboard'): string => {
    return `narrations/${narType}/${id}.mp3`;
  };

  const path = getNarrationPath(standardizedNarrationId, type);
  console.log(`[NARRATION] Uploading audio to ${path}`);
  
  try {
    // Use the storage proxy to handle uploads
    const downloadURL = await uploadToStorage(audioBlob, path, {
      contentType: audioBlob.type || 'audio/mpeg',
      customMetadata: {
        narrationId: standardizedNarrationId,
        type
      }
    });
    
    console.log(`[NARRATION] Upload successful, URL: ${downloadURL}`);
    return downloadURL;
  } catch (error) {
    console.error('[NARRATION] Upload failed:', error);
    throw error;
  }
};

/**
 * Migrate audio files from old path structure to new path structure
 * This maintains compatibility with existing code while preparing for the new structure
 */
const migrateNarrationAudio = async (): Promise<{
  success: string[];
  skipped: string[];
  failed: string[];
}> => {
  try {
    const success: string[] = [];
    const skipped: string[] = [];
    const failed: string[] = [];

    // Check if Firebase is connected
    try {
      const testRef = doc(presentationDb, 'narrations', 'test-connection');
      await getDoc(testRef);
      console.log('Firebase connection successful');
    } catch (error) {
      console.error('Firebase connection failed:', error);
      return { success, skipped, failed: ['Firebase connection failed'] };
    }

    // Migrate slide narrations
    console.log('[NARRATION] Starting migration of slide narrations...');
    const slidesRef = ref(storage, 'audio/slides');
    const slideFiles = await listAll(slidesRef);

    for (const fileRef of slideFiles.items) {
      const oldPath = fileRef.fullPath;
      const fileName = fileRef.name;
      const newPath = `narrations/slide/${fileName}`;
      
      try {
        // Check if file already exists in new location
        const newRef = ref(storage, newPath);
        try {
          await getDownloadURL(newRef);
          console.log(`[NARRATION] File already exists in new location: ${newPath}`);
          skipped.push(oldPath);
          continue;
        } catch {
          // File doesn't exist in new location, proceed with migration
        }

        // Download the file from old location
        const url = await getDownloadURL(fileRef);
        const response = await fetch(url);
        const blob = await response.blob();

        // Upload to new location
        await uploadBytes(newRef, blob, {
          contentType: 'audio/mpeg',
          customMetadata: {
            migrated: 'true',
            originalPath: oldPath,
            migratedAt: new Date().toISOString()
          }
        });

        console.log(`[NARRATION] Successfully migrated: ${oldPath} -> ${newPath}`);
        success.push(oldPath);
      } catch (error) {
        console.error(`[NARRATION] Failed to migrate ${oldPath}:`, error);
        failed.push(oldPath);
      }
    }

    // Migrate dashboard narrations
    console.log('\n[NARRATION] Starting migration of dashboard narrations...');
    const dashboardRef = ref(storage, 'audio/dashboard');
    const dashboardFiles = await listAll(dashboardRef);

    for (const fileRef of dashboardFiles.items) {
      const oldPath = fileRef.fullPath;
      const fileName = fileRef.name;
      const newPath = `narrations/dashboard/${fileName}`;
      
      try {
        // Check if file already exists in new location
        const newRef = ref(storage, newPath);
        try {
          await getDownloadURL(newRef);
          console.log(`[NARRATION] File already exists in new location: ${newPath}`);
          skipped.push(oldPath);
          continue;
        } catch {
          // File doesn't exist in new location, proceed with migration
        }

        // Download the file from old location
        const url = await getDownloadURL(fileRef);
        const response = await fetch(url);
        const blob = await response.blob();

        // Upload to new location
        await uploadBytes(newRef, blob, {
          contentType: 'audio/mpeg',
          customMetadata: {
            migrated: 'true',
            originalPath: oldPath,
            migratedAt: new Date().toISOString()
          }
        });

        console.log(`[NARRATION] Successfully migrated: ${oldPath} -> ${newPath}`);
        success.push(oldPath);
      } catch (error) {
        console.error(`[NARRATION] Failed to migrate ${oldPath}:`, error);
        failed.push(oldPath);
      }
    }

    // Print summary
    console.log('\n[NARRATION] Migration Summary:');
    console.log(`Successfully migrated: ${success.length} files`);
    console.log(`Skipped (already exists): ${skipped.length} files`);
    console.log(`Failed: ${failed.length} files`);

    if (failed.length > 0) {
      console.log('\nFailed files:');
      failed.forEach(path => console.log(`- ${path}`));
    }

    return { success, skipped, failed };
  } catch (error) {
    console.error('[NARRATION] Migration failed:', error);
    throw error;
  }
};

/**
 * Generate phonetic text from narration text
 * This applies specific phonetic transformations for acronyms and irregular nouns
 * to ensure consistent pronunciation by ElevenLabs TTS
 */
const generatePhoneticText = async (narrationId: string, text: string): Promise<string> => {
  try {
    // Simple phonetic text generation - replace with more sophisticated algorithm if needed
    // This is a placeholder for a more advanced phonetic text generator
    let phoneticText = text;
    
    // Apply common phonetic transformations
    phoneticText = phoneticText
      // Ensure SentientIQ is spelled correctly (not SentienIQ)
      .replace(/SentienIQ/g, 'SentientIQ')
      .replace(/sentieniq/gi, 'SentientIQ')
      // Handle common medical abbreviations
      .replace(/Dr\./g, 'Doctor')
      .replace(/\bMD\b/g, 'M D')
      .replace(/\bEHR\b/g, 'E H R')
      .replace(/\bAI\b/g, 'A I')
      .replace(/\bML\b/g, 'M L')
      .replace(/\bJIMI\b/g, 'Jimmy');
    
    // Update the narration in Firebase with the phonetic text
    const narrationRef = doc(presentationDb, 'narrations', narrationId);
    await updateDoc(narrationRef, {
      phoneticNarration: phoneticText,
      lastUpdated: serverTimestamp()
    });
    
    return phoneticText;
  } catch (error) {
    console.error('Error generating phonetic text:', error);
    return text; // Return original text as fallback
  }
};

// Migrate all narrations from legacy collections to unified collection
export const migrateToUnifiedCollection = async (): Promise<{ 
  migrated: number, 
  deleted: number, 
  errors: number 
}> => {
  let migrated = 0;
  let deleted = 0;
  let errors = 0;

  try {
    console.log('Starting migration to unified narrations collection...');
    
    // Step 1: Migrate from slide_narrations collection
    const slideNarrationsRef = collection(presentationDb, 'slide_narrations');
    const slideNarrationsSnapshot = await getDocs(slideNarrationsRef);
    
    console.log(`Found ${slideNarrationsSnapshot.size} narrations in slide_narrations collection`);
    
    // Process each slide narration
    for (const docSnapshot of slideNarrationsSnapshot.docs) {
      try {
        const data = docSnapshot.data();
        const narrationId = docSnapshot.id;
        
        // Create a properly formatted narration object
        const slideNarration = createSlideNarration(
          narrationId,
          data.narration || data.text || '',
          data.title || `Slide ${narrationId}`,
          data.hasDynamicContent || false,
          data.phoneticNarration || '',
          data.status || 'pending',
          data.audioUrl || null
        );
        
        // Save to unified collection
        const success = await syncNarrationToFirebase(slideNarration);
        
        if (success) {
          // Delete from legacy collection
          const legacyDocRef = doc(presentationDb, 'slide_narrations', narrationId);
          await deleteDoc(legacyDocRef);
          migrated++;
          deleted++;
          console.log(`Migrated and deleted slide narration: ${narrationId}`);
          
          // Generate phonetic text if not already present
          if (!data.phoneticNarration) {
            await generatePhoneticText(narrationId, data.narration || data.text || '');
            console.log(`Generated phonetic text for narration: ${narrationId}`);
          }
        } else {
          errors++;
          console.error(`Failed to migrate slide narration: ${narrationId}`);
        }
      } catch (error) {
        errors++;
        console.error(`Error migrating slide narration ${docSnapshot.id}:`, error);
      }
    }
    
    // Step 2: Migrate from dashboard_narrations collection
    const dashboardNarrationsRef = collection(presentationDb, 'dashboard_narrations');
    const dashboardNarrationsSnapshot = await getDocs(dashboardNarrationsRef);
    
    console.log(`Found ${dashboardNarrationsSnapshot.size} narrations in dashboard_narrations collection`);
    
    // Process each dashboard narration
    for (const docSnapshot of dashboardNarrationsSnapshot.docs) {
      try {
        const data = docSnapshot.data();
        const narrationId = docSnapshot.id;
        
        // Create a properly formatted narration object
        const dashboardNarration = createDashboardNarration(
          narrationId,
          data.text || '',
          data.title || `Dashboard ${narrationId}`,
          data.script || '',
          data.triggerPoint || 'onLoad',
          data.phoneticNarration || '',
          data.status || 'pending',
          data.audioUrl || null
        );
        
        // Save to unified collection
        const success = await syncNarrationToFirebase(dashboardNarration);
        
        if (success) {
          // Delete from legacy collection
          const legacyDocRef = doc(presentationDb, 'dashboard_narrations', narrationId);
          await deleteDoc(legacyDocRef);
          migrated++;
          deleted++;
          console.log(`Migrated and deleted dashboard narration: ${narrationId}`);
          
          // Generate phonetic text if not already present
          if (!data.phoneticNarration) {
            await generatePhoneticText(narrationId, data.text || '');
            console.log(`Generated phonetic text for narration: ${narrationId}`);
          }
        } else {
          errors++;
          console.error(`Failed to migrate dashboard narration: ${narrationId}`);
        }
      } catch (error) {
        errors++;
        console.error(`Error migrating dashboard narration ${docSnapshot.id}:`, error);
      }
    }
    
    console.log(`Migration completed: ${migrated} migrated, ${deleted} deleted, ${errors} errors`);
    return { migrated, deleted, errors };
  } catch (error) {
    console.error('Error during narration migration:', error);
    return { migrated, deleted, errors: errors + 1 };
  }
};

// Sync all narrations from the script to Firebase
const syncAllNarrationsToFirebase = async (): Promise<{ successCount: number, errorCount: number }> => {
  let successCount = 0;
  let errorCount = 0;
  
  try {
    console.log('Starting synchronization of all narrations to Firebase...');
    
    // Process slide narrations
    for (const narration of narrationScript) {
      try {
        const slideNarration = createSlideNarration(
          narration.slideId, // Use slideId instead of id
          narration.narration,
          narration.title,
          narration.hasDynamicContent || false,
          getPhoneticText({ text: narration.narration } as any),
          'pending',
          null
        );
        
        const success = await syncNarrationToFirebase(slideNarration);
        
        if (success) {
          successCount++;
          console.log(`Synchronized slide narration: ${narration.slideId}`);
        } else {
          errorCount++;
          console.error(`Failed to sync slide narration: ${narration.slideId}`);
        }
      } catch (error) {
        console.error(`Error syncing slide narration ${narration.slideId}:`, error);
        errorCount++;
      }
    }
    
    // Process dashboard narrations
    for (const narration of dashboardNarrationScript) {
      try {
        const dashboardNarration = createDashboardNarration(
          narration.id,
          narration.narration,
          narration.title,
          '',
          narration.triggerPoint || 'onLoad',
          getPhoneticText({ text: narration.narration } as any),
          'pending',
          null
        );
        
        const success = await syncNarrationToFirebase(dashboardNarration);
        
        if (success) {
          successCount++;
          console.log(`Synchronized dashboard narration: ${narration.id}`);
        } else {
          errorCount++;
          console.error(`Failed to sync dashboard narration: ${narration.id}`);
        }
      } catch (error) {
        console.error(`Error syncing dashboard narration ${narration.id}:`, error);
        errorCount++;
      }
    }
    
    console.log(`Narration synchronization completed: ${successCount} success, ${errorCount} errors`);
    return { successCount, errorCount };
  } catch (error) {
    console.error('Error during narration synchronization:', error);
    return { successCount, errorCount: errorCount + 1 };
  }
};

/**
 * Maps audio files in Firebase Storage to their corresponding narration documents in Firestore
 * and updates any narrations that have audio files but are missing the audioUrl field.
 * Handles non-standardized file names by using flexible matching algorithms.
 * Updates narration status based on the mapping results.
 * 
 * @returns Promise with the results of the mapping operation
 */
export const mapNarrationsToAudioFiles = async (): Promise<{
  total: number;
  updated: number;
  errors: number;
  unmapped: string[];
  statusUpdated: number;
}> => {
  let total = 0;
  let updated = 0;
  let errors = 0;
  let statusUpdated = 0;
  const unmapped: string[] = [];
  
  try {
    console.log('Starting narration to audio file mapping...');
    
    // Get all narrations from Firestore
    const narrationsRef = collection(presentationDb, 'narrations');
    const narrationsSnapshot = await getDocs(narrationsRef);
    const narrations = new Map<string, any>();
    const narrationsBySlideId = new Map<string, any>();
    
    narrationsSnapshot.forEach(doc => {
      const narrationData = { id: doc.id, ...doc.data() } as any;
      narrations.set(doc.id, narrationData);
      
      // Also index by slideId for slide narrations
      if (narrationData.type === 'slide' && narrationData.slideId) {
        narrationsBySlideId.set(narrationData.slideId, narrationData);
      }
    });
    
    console.log(`Found ${narrations.size} narrations in Firestore`);
    
    // List all audio files in the slide narrations directory
    const slideAudioRef = ref(storage, 'narrations/slide');
    let slideAudioFiles: string[] = [];
    
    try {
      const slideAudioList = await listAll(slideAudioRef);
      slideAudioFiles = slideAudioList.items.map(item => item.name);
      console.log(`Found ${slideAudioFiles.length} audio files in slide narrations directory`);
    } catch (error) {
      console.warn('Error listing slide audio files, directory may not exist:', error);
    }
    
    // List all audio files in the dashboard narrations directory
    const dashboardAudioRef = ref(storage, 'narrations/dashboard');
    let dashboardAudioFiles: string[] = [];
    
    try {
      const dashboardAudioList = await listAll(dashboardAudioRef);
      dashboardAudioFiles = dashboardAudioList.items.map(item => item.name);
      console.log(`Found ${dashboardAudioFiles.length} audio files in dashboard narrations directory`);
    } catch (error) {
      console.warn('Error listing dashboard audio files, directory may not exist:', error);
    }
    
    // Helper function to find the best matching narration for an audio file
    const findMatchingNarration = (audioFileName: string, type: 'slide' | 'dashboard'): { narrationId: string; narration: any } | null => {
      // Remove .mp3 extension
      const baseFileName = audioFileName.replace(/\.mp3$/i, '');
      
      // Try exact match first
      if (narrations.has(baseFileName)) {
        return { narrationId: baseFileName, narration: narrations.get(baseFileName) };
      }
      
      // For slide narrations, try matching by slideId
      if (type === 'slide' && narrationsBySlideId.has(baseFileName)) {
        const narration = narrationsBySlideId.get(baseFileName);
        return { narrationId: narration.id, narration };
      }
      
      // Try matching with common prefixes/suffixes removed
      const normalizedFileName = baseFileName
        .replace(/^slide-/i, '') // Remove 'slide-' prefix
        .replace(/^dashboard-/i, '') // Remove 'dashboard-' prefix
        .replace(/-narration$/i, '') // Remove '-narration' suffix
        .replace(/-audio$/i, ''); // Remove '-audio' suffix
      
      // Look for partial matches
      for (const [id, narration] of narrations.entries()) {
        if (narration.type !== type) continue;
        
        const normalizedId = id
          .replace(/^slide-/i, '')
          .replace(/^dashboard-/i, '')
          .replace(/-narration$/i, '')
          .replace(/-audio$/i, '');
        
        // Check if normalized strings match
        if (normalizedId === normalizedFileName) {
          return { narrationId: id, narration };
        }
        
        // Check if one contains the other
        if (normalizedId.includes(normalizedFileName) || normalizedFileName.includes(normalizedId)) {
          return { narrationId: id, narration };
        }
        
        // For slide narrations, also check slideId
        if (type === 'slide' && narration.slideId) {
          const normalizedSlideId = narration.slideId
            .replace(/^slide-/i, '')
            .replace(/-narration$/i, '')
            .replace(/-audio$/i, '');
          
          if (normalizedSlideId === normalizedFileName ||
              normalizedSlideId.includes(normalizedFileName) ||
              normalizedFileName.includes(normalizedSlideId)) {
            return { narrationId: id, narration };
          }
        }
      }
      
      return null;
    };

    // Create a set to track narrations that have been processed
    const processedNarrations = new Set<string>();
    
    // Process slide audio files
    for (const audioFile of slideAudioFiles) {
      total++;
      const match = findMatchingNarration(audioFile, 'slide');
      
      if (match) {
        const { narrationId, narration } = match;
        processedNarrations.add(narrationId);
        
        if (!narration.audioUrl) {
          try {
            // Get the download URL for the audio file
            const audioRef = ref(storage, `narrations/slide/${audioFile}`);
            const downloadURL = await getDownloadURL(audioRef);
            
            // Update the narration document with the audio URL
            const narrationRef = doc(presentationDb, 'narrations', narrationId);
            await updateDoc(narrationRef, {
              audioUrl: downloadURL,
              status: 'uploaded',
              lastUpdated: serverTimestamp()
            });
            
            updated++;
            statusUpdated++;
            console.log(`Updated slide narration ${narrationId} with audio URL from file ${audioFile}`);
          } catch (error) {
            errors++;
            // Update status to error if we couldn't get the download URL
            try {
              const narrationRef = doc(presentationDb, 'narrations', narrationId);
              await updateDoc(narrationRef, {
                status: 'error',
                lastUpdated: serverTimestamp()
              });
              statusUpdated++;
              console.log(`Updated slide narration ${narrationId} status to error due to download failure`);
            } catch (updateError) {
              console.error(`Error updating status for narration ${narrationId}:`, updateError);
            }
            console.error(`Error updating slide narration ${narrationId} with file ${audioFile}:`, error);
          }
        } else if (narration.status !== 'uploaded') {
          // If narration has audioUrl but status is not 'uploaded', update it
          try {
            const narrationRef = doc(presentationDb, 'narrations', narrationId);
            await updateDoc(narrationRef, {
              status: 'uploaded',
              lastUpdated: serverTimestamp()
            });
            statusUpdated++;
            console.log(`Updated slide narration ${narrationId} status to 'uploaded'`);
          } catch (error) {
            console.error(`Error updating status for narration ${narrationId}:`, error);
          }
        } else {
          console.log(`Slide narration ${narrationId} already has an audio URL and correct status`);
        }
      } else {
        unmapped.push(`slide/${audioFile}`);
        console.warn(`Could not find matching narration document for audio file: slide/${audioFile}`);
      }
    }
    
    // Process dashboard audio files
    for (const audioFile of dashboardAudioFiles) {
      total++;
      const match = findMatchingNarration(audioFile, 'dashboard');
      
      if (match) {
        const { narrationId, narration } = match;
        processedNarrations.add(narrationId);
        
        if (!narration.audioUrl) {
          try {
            // Get the download URL for the audio file
            const audioRef = ref(storage, `narrations/dashboard/${audioFile}`);
            const downloadURL = await getDownloadURL(audioRef);
            
            // Update the narration document with the audio URL
            const narrationRef = doc(presentationDb, 'narrations', narrationId);
            await updateDoc(narrationRef, {
              audioUrl: downloadURL,
              status: 'uploaded',
              lastUpdated: serverTimestamp()
            });
            
            updated++;
            statusUpdated++;
            console.log(`Updated dashboard narration ${narrationId} with audio URL from file ${audioFile}`);
          } catch (error) {
            errors++;
            // Update status to error if we couldn't get the download URL
            try {
              const narrationRef = doc(presentationDb, 'narrations', narrationId);
              await updateDoc(narrationRef, {
                status: 'error',
                lastUpdated: serverTimestamp()
              });
              statusUpdated++;
              console.log(`Updated dashboard narration ${narrationId} status to error due to download failure`);
            } catch (updateError) {
              console.error(`Error updating status for narration ${narrationId}:`, updateError);
            }
            console.error(`Error updating dashboard narration ${narrationId} with file ${audioFile}:`, error);
          }
        } else if (narration.status !== 'uploaded') {
          // If narration has audioUrl but status is not 'uploaded', update it
          try {
            const narrationRef = doc(presentationDb, 'narrations', narrationId);
            await updateDoc(narrationRef, {
              status: 'uploaded',
              lastUpdated: serverTimestamp()
            });
            statusUpdated++;
            console.log(`Updated dashboard narration ${narrationId} status to 'uploaded'`);
          } catch (error) {
            console.error(`Error updating status for narration ${narrationId}:`, error);
          }
        } else {
          console.log(`Dashboard narration ${narrationId} already has an audio URL and correct status`);
        }
      } else {
        unmapped.push(`dashboard/${audioFile}`);
        console.warn(`Could not find matching narration document for audio file: dashboard/${audioFile}`);
      }
    }

    // Update status for narrations that don't have audio files
    for (const [narrationId, narration] of narrations.entries()) {
      if (!processedNarrations.has(narrationId) && !narration.audioUrl && narration.status !== 'pending') {
        try {
          const narrationRef = doc(presentationDb, 'narrations', narrationId);
          await updateDoc(narrationRef, {
            status: 'pending',
            lastUpdated: serverTimestamp()
          });
          statusUpdated++;
          console.log(`Updated narration ${narrationId} status to 'pending' (no audio file found)`);
        } catch (error) {
          console.error(`Error updating status for narration ${narrationId}:`, error);
        }
      }
    }
    
    console.log(`Mapping completed: ${total} total files, ${updated} narrations updated, ${statusUpdated} statuses updated, ${errors} errors, ${unmapped.length} unmapped files`);
    
    return { total, updated, errors, unmapped, statusUpdated };
  } catch (error) {
    console.error('Error mapping narrations to audio files:', error);
    return { total, updated, errors, unmapped, statusUpdated };
  }
};

// Export all functions and types
export {
  getNarrationText,
  getNarrationTitle,
  getPhoneticText,
  createBaseNarration,
  createSlideNarration,
  createDashboardNarration,
  syncNarrationToFirebase,
  fetchNarrations,
  updateNarrationWithAudio,
  useNarrationSync,
  uploadNarrationAudio,
  migrateNarrationAudio,
  syncAllNarrationsToFirebase,
  generatePhoneticText
  // mapNarrationsToAudioFiles and migrateToUnifiedCollection are exported directly in their declarations
};
